// @flow
import upperCase from 'lodash/upperCase'
import type { Profile } from 'types'
import type { ProfileLanguage, ProfilePhilosophy } from 'types/profile'
import { format as formatAge } from 'util/age'
import { format as formatTime } from 'util/time'
import { uuidToUrl } from 'util/uploadcare'

export const DEFAULT_IMAGE_URL = uuidToUrl('d0ac49cc-3423-4412-9347-aa8c0f973107')

export const LANGUAGE_OPTIONS: { [ProfileLanguage]: string } = {
  french: 'French',
  mandarin: 'Mandarin',
  russian: 'Russian',
  spanish: 'Spanish',
  other: 'Other',
}

export const PHILOSOPHY_OPTIONS: { [ProfilePhilosophy]: string } = {
  hidden: 'Hidden',
  play_based: 'Play-based',
  nature_based: 'Nature-based',
  waldorf: 'Waldorf',
  waldorf_inspired: 'Waldorf-inspired',
  montessori: 'Montessori',
  montessori_inspired: 'Montessori-inspired',
  reggio_emilia_inspired: 'Reggio Emilia-inspired',
  rie_inspired: 'RIE-inspired',
  stem: 'STEM',
  steam: 'STEAM',
  bilingual: 'Bilingual',
  language_immersion: 'Language immersion',
}

export const PHILOSOPHY_LABELS: { [ProfilePhilosophy]: string } = {
  hidden: 'Hidden',
  play_based: 'play-based',
  nature_based: 'nature-based',
  waldorf: 'Waldorf',
  waldorf_inspired: 'Waldorf-inspired',
  montessori: 'Montessori',
  montessori_inspired: 'Montessori-inspired',
  reggio_emilia_inspired: 'Reggio Emilia-inspired',
  rie_inspired: 'RIE-inspired',
  stem: 'STEM',
  steam: 'STEAM',
  bilingual: 'bilingual',
  language_immersion: 'language immersion',
}

// Descriptions shown on public-facing content (i.e. profile content)
export const PHILOSOPHY_PUBLIC_DESCRIPTIONS: { [ProfilePhilosophy]: string } = {
  play_based:
    'My program follows a play-based philosophy. I believe that children learn social, cognitive, and emotional skills by exploring new experiences. My environment is responsive to the children in our care, and allows children’s own interests to help facilitate their learning and engagement.',
  nature_based:
    'My program follows a nature-based philosophy. Children will spend all or most of their time outdoors. I believe that nature is the best environment for children and supports their development as they prepare to enter more formal school systems in kindergarten and beyond.',
  waldorf:
    'I am a certified Waldorf teacher. My program emphasizes the role of imagination in learning, striving to holistically integrate the intellectual, practical, and artistic development of pupils.',
  waldorf_inspired:
    'I am inspired by the Waldorf philosophy. My program emphasizes the role of imagination in learning, striving to holistically integrate the intellectual, practical, and artistic development of pupils.',
  montessori:
    'I am a certified Montessori teacher. I believe that children are naturally eager for knowledge and capable of initiating learning in a supportive, thoughtful-prepared environment.',
  montessori_inspired:
    'I am inspired by the Montessori philosophy. I believe that children are naturally eager for knowledge and capable of initiating learning in a supportive, thoughtful-prepared environment.',
  reggio_emilia_inspired:
    'I am inspired by the Reggio Emilia philosophy. I believe that children should guide their own learning through experiences of touching, moving, listening, and observing. My environment gives children endless ways and opportunities to express themselves.',
  rie_inspired:
    'I am inspired by the RIE (Resources for Infant Educarers) philosophy. I believe in demonstrating respect every time I interact with children, no matter how young. Each child is a unique human being, who should feel secure, autonomous, competent, and connected.',
  stem: 'My program emphasizes exploring the basic concepts of Science, Technology, Engineering, and Mathematics. I believe that these topics provide a strong basis for cognitive growth and prepare children for the modern world. I have researched S.T.E.M. extensively and will prepare curriculum based on S.T.E.M. activities.',
  steam:
    'My program emphasizes exploring the basic concepts of Science, Technology, Engineering, Art, and Mathematics. I believe that these topics provide a strong basis for cognitive, social, and emotional growth, as well as preparing children for the modern world. I have researched S.T.E.A.M. extensively and will prepare curriculum based on S.T.E.A.M. activities.',
  bilingual:
    "Children in my program will hear and speak another language in addition to English. I believe that learning a second language at a young age encourages a child's intellectual and social development.",
  language_immersion:
    "Daily activities and care will be provided entirely in one language other than English. I believe that learning a new language at a young age encourages a child's intellectual and social development. In addition, I believe that total immersion is the best way to acquire a second language.",
}

// Descriptions shown internally to directors
export const PHILOSOPHY_DIRECTOR_DESCRIPTIONS: { [ProfilePhilosophy]: string } = {
  play_based:
    'My program follows a play-based philosophy. I believe that children learn social, cognitive, and emotional skills by exploring new experiences. My environment is responsive to the children in our care, and allows children’s own interests to help facilitate their learning and engagement.',
  nature_based:
    'My program follows a nature-based philosophy. Children will spend all or most of their time outdoors. I believe that nature is the best environment for children and supports their development as they prepare to enter more formal school systems in kindergarten and beyond.',
  waldorf:
    'I am a certified Waldorf teacher. My program emphasizes the role of imagination in learning, striving to holistically integrate the intellectual, practical, and artistic development of pupils.',
  waldorf_inspired:
    'I have taken some classes about the Waldorf philosophy and/or researched it thoroughly, but I am not a certified Waldorf teacher. My program emphasizes the role of imagination in learning, striving to holistically integrate the intellectual, practical, and artistic development of pupils.',
  montessori:
    'I am a certified Montessori teacher. I believe that children are naturally eager for knowledge and capable of initiating learning in a supportive, thoughtful-prepared environment.',
  montessori_inspired:
    'I have taken some classes about the Montessori philosophy and/or researched it thoroughly, but I am not a certified Montessori teacher. I believe that children are naturally eager for knowledge and capable of initiating learning in a supportive, thoughtful-prepared environment.',
  reggio_emilia_inspired:
    'I have taken some classes about the Reggio Emilia philosophy and/or researched it thoroughly. I believe that children should guide their own learning through experiences of touching, moving, listening, and observing. My environment gives children endless ways and opportunities to express themselves.',
  rie_inspired:
    'I have taken some classes about the RIE philosophy and/or researched it thoroughly. I believe in demonstrating respect every time I interact with children, no matter how young. Each child is a unique human being, who should feel secure, autonomous, competent, and connected.',
  stem: 'My program emphasizes exploring the basic concepts of Science, Technology, Engineering, and Mathematics. I believe that these topics provide a strong basis for cognitive growth and prepare children for the modern world. I have researched S.T.E.M. extensively and will prepare curriculum based on S.T.E.M. activities.',
  steam:
    'My program emphasizes exploring the basic concepts of Science, Technology, Engineering, Art, and Mathematics. I believe that these topics provide a strong basis for cognitive, social, and emotional growth, as well as preparing children for the modern world. I have researched S.T.E.A.M. extensively and will prepare curriculum based on S.T.E.A.M. activities.',
  bilingual:
    "Children in my program will hear and speak another language in addition to English. I believe that learning a second language at a young age encourages a child's intellectual and social development.",
  language_immersion:
    "Daily activities and care will be provided entirely in one language other than English. I believe that learning a new language at a young age encourages a child's intellectual and social development. In addition, I believe that total immersion is the best way to acquire a second language.",
}

export const PHILOSOPHIES_WITH_LANGUAGE: ProfilePhilosophy[] = ['bilingual', 'language_immersion']

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

export function formatAges(profile: { ageMonthsMin: number | null, ageMonthsMax: number | null }) {
  const { ageMonthsMin, ageMonthsMax } = profile
  if (ageMonthsMin === null) return null
  if (ageMonthsMax === null) return null

  return `${formatAge(ageMonthsMin)} - ${formatAge(ageMonthsMax)}`
}

export function formatDaysOfWeek(profile: Profile) {
  if (profile.daysOfWeek.every(x => !x)) return null

  // format MTWThF as "Mon - Fri"
  const specialCase = [false, true, true, true, true, true, false]
  if (profile.daysOfWeek.every((x, i) => x === specialCase[i])) {
    return `${DAYS_OF_WEEK[1]} - ${DAYS_OF_WEEK[5]}`
  }

  return profile.daysOfWeek
    .map((flag, index) => (flag ? DAYS_OF_WEEK[index] : null))
    .filter(value => value)
    .join(', ')
}

export function formatHours(profile: { hoursStart: string | null, hoursEnd: string | null }) {
  const { hoursStart, hoursEnd } = profile
  if (hoursStart === null) return null
  if (hoursEnd === null) return null

  return `${formatTime(hoursStart)} to ${formatTime(hoursEnd)}`
}

export function formatLanguage(profile: Profile) {
  if (profile.language === null) return null
  if (profile.language === 'other') return profile.languageOther

  return LANGUAGE_OPTIONS[profile.language]
}

export function formatMealsProvided(profile: Profile) {
  if (profile.mealsBreakfastProvided === null) return null
  if (profile.mealsLunchProvided === null) return null
  if (profile.mealsDinnerProvided === null) return null

  const parts = []
  if (profile.mealsBreakfastProvided) parts.push('Breakfast')
  if (profile.mealsLunchProvided) parts.push('Lunch')
  if (profile.mealsDinnerProvided) parts.push('Dinner')
  return parts.length ? parts.join(', ') : 'No meals'
}

export function formatPhilosophy(profile: Profile) {
  const { philosophy, philosophySecondary } = profile
  if (philosophy === null && philosophySecondary === null) return null

  let formatted = ''

  if (philosophy !== null) {
    if (isLanguageApplicable(profile)) {
      formatted += formatLanguage(profile) || ''
    }

    if (formatted === '') {
      const label = PHILOSOPHY_LABELS[philosophy]
      formatted += upperCase(label.charAt(0)) + label.substring(1)
    } else {
      formatted += ` ${PHILOSOPHY_LABELS[philosophy]}`
    }
  }

  if (philosophySecondary !== null) {
    formatted += `${philosophy === null ? '' : ', '}${philosophySecondary}`
  }

  return formatted
}

export function formatPottyTrainingRequired(profile: Profile) {
  if (profile.pottyTrainingRequired === null) return null

  return `Potty training ${profile.pottyTrainingRequired ? 'required' : 'not required'}`
}

export function formatProviderChildRatio(profile: Profile) {
  if (profile.providerChildRatioConsequent === null) return null

  const antecedent = profile.providerChildRatioAntecedent
  const consequent = profile.providerChildRatioConsequent
  return `${antecedent}:${consequent} providers to children`
}

export function formatSnacksProvidedCount(profile: Profile) {
  if (profile.snacksProvidedCount === null) return null
  if (profile.snacksProvidedCount === 0) return 'No snacks'

  return `${profile.snacksProvidedCount} snacks per day`
}

export function formatSubsidizedCareAccepted(profile: Profile) {
  if (!profile.subsidizedCareAccepted) return null

  return `Subsidized care accepted`
}

export function isLanguageApplicable(profile: Profile) {
  return PHILOSOPHIES_WITH_LANGUAGE.includes(profile.philosophy)
}

export function primaryImage(profile: Profile) {
  return (profile.profileMedia[0] && profile.profileMedia[0].media.url) || DEFAULT_IMAGE_URL
}
